import * as SentryWeb from "@sentry/react";
import * as Sentry from "@sentry/react-native";
import { Platform } from "react-native";

import { applyRelayFetchContextToSentryEvent } from "@whoosh/relay/src";

import { getExpoEASConstant } from "../expoConstants";

const replayNetworkDetailAllowUrls = [
  "https://api.app.whoosh.io/private/api",
  "https://api.demo.whoosh.io/private/api",
];

/**
 * Handles Sentry Initialization.
 *
 * **NOTE** User identification for sentry lives in the useSentryUserIdentification file (there is a native and web
 * version of that file).
 *
 */

let hasInitializedSentry = false;

export const initializeSentry = () => {
  if (hasInitializedSentry) {
    return;
  }

  const deployEnv = getExpoEASConstant("deployEnv");
  if (deployEnv !== "local" && deployEnv !== "e2e") {
    const isProductionEnvironment = deployEnv === "prod";
    const sentryDSN = getExpoEASConstant("sentryDsn");

    Sentry.init({
      dsn: sentryDSN ?? "",
      debug: deployEnv !== "prod", // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production,
      integrations:
        Platform.OS === "web"
          ? [
              SentryWeb.replayIntegration({
                maskAllText: false,
                networkDetailAllowUrls: replayNetworkDetailAllowUrls,
              }),
              SentryWeb.browserTracingIntegration(),
            ]
          : [
              Sentry.mobileReplayIntegration({
                maskAllText: false,
                maskAllImages: false,
                maskAllVectors: false,
                networkDetailAllowUrls: replayNetworkDetailAllowUrls,
                networkCaptureBodies: true,
              }),
            ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: [
        "api.app.whoosh.io",
        "api.demo.whoosh.io",
        "api.replica.whoosh.io",
        "api.staging.whoosh.io",
        "app.whoosh.io",
      ],
      environment: deployEnv,
      _experiments: {
        replaysSessionSampleRate: isProductionEnvironment ? 0.8 : 1.0,
        replaysOnErrorSampleRate: 1.0,
      },
      beforeSend(event) {
        //Modify or filter or modify sentry events before they send
        //If you return null, the error will be ignored
        // Currently this is just ignoring our invalid session errors. But in the future we might
        // extend this into a full module
        const shouldIgnoreError =
          event.exception?.values?.findIndex((e) => {
            return (
              errorValuesToIgnore.findIndex((value) => {
                return e.value?.includes(value);
              }) !== -1
            );
          }) !== -1;
        if (shouldIgnoreError) {
          return null;
        }
        return event;
      },
    });

    Sentry.addEventProcessor(applyRelayFetchContextToSentryEvent);

    if (Platform.OS === "web") {
      SentryWeb.addEventProcessor(applyRelayFetchContextToSentryEvent);
    }
    hasInitializedSentry = true;
  }
};

export const disableSentry = () => {
  if (!hasInitializedSentry) {
    return;
  }

  hasInitializedSentry = false;
  void (Platform.OS === "web" ? SentryWeb.close() : Sentry.close());
};

const getSentryProjectId = (dsn: string) => {
  const parsedDsn = new URL(dsn);
  return parsedDsn.pathname.replace("/", "");
};

const getWebSentryDsn = () => {
  return getExpoEASConstant("sentryDsn") ?? "";
};

const getActiveReplayId = () => {
  const replayIntegration = SentryWeb.getClient()?.getIntegrationByName(
    "Replay"
  ) as
    | {
        getReplayId?: () => string | undefined;
      }
    | undefined;

  return replayIntegration?.getReplayId?.() || null;
};

export const getSentryReplayUrl = () => {
  const replayId = getActiveReplayId();
  if (!replayId) {
    return null;
  }

  const sentryDsn = getWebSentryDsn();
  const sentryOrganization = getExpoEASConstant("sentryOrganization");
  if (!sentryDsn || !sentryOrganization) {
    return null;
  }

  const projectId = getSentryProjectId(sentryDsn);
  return `https://${sentryOrganization}.sentry.io/explore/replays/${replayId}/?project=${projectId}`;
};

export const isSentryReplayCopyShortcut = (event: KeyboardEvent) => {
  const keyPressed = event.key.toLowerCase();
  return keyPressed === "l" && event.shiftKey && !event.altKey && event.ctrlKey;
};

export const isEditableEventTarget = (target: EventTarget | null) => {
  if (typeof HTMLElement === "undefined") {
    return false;
  }

  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const elementTag = target.tagName.toLowerCase();
  return (
    elementTag === "input" ||
    elementTag === "textarea" ||
    elementTag === "select" ||
    target.isContentEditable ||
    target.contentEditable === "true"
  );
};

export const copySentryReplayUrlToClipboard = async () => {
  const replayUrl = getSentryReplayUrl();
  if (!replayUrl) {
    throw new Error("No Active Sentry Replay");
  }

  if (!navigator.clipboard?.writeText) {
    throw new Error("Clipboard API is unavailable");
  }

  await navigator.clipboard.writeText(replayUrl);
  return replayUrl;
};

/**
 * WHY DID WE FILTER THESE?
 *
 * error in gql - this is relay throwing an error. Our error handler will also report a more specific error so this is duplicate.
 * your session is... - these are just expired sessions - no need to report that to sentry
 */
const errorValuesToIgnore = [
  "error in gql",
  "Your session is either invalid or has expired",
];
